import React from "react";
import { Box } from "@salestrak/ui";

import { useTransientFlag } from "#app/lib/hooks/use-transient-value";

/** How long a jumped-to row wears the selected wash before it fades — the
 *  same flash the keyboard-shortcuts reference uses. */
const FLASH_MS = 1600;
/** How long a requested highlight stays claimable. The target row may mount
 *  well after the request — the page behind it can be showing its skeleton
 *  while settings load — so the window is generous; past it, a request that
 *  never found its row must not flash some later visit. */
const FRESH_MS = 8000;

type PendingHighlight = { anchor: string; at: number };

// Module-level one-shot store, not a context: the requester (the settings
// sidebar) and the consumer (a row on whatever page the router mounts next)
// share no tree, and the request must survive the route transition.
let pending: PendingHighlight | null = null;
const listeners = new Set<() => void>();

/** Ask the next-mounted highlight target with this anchor to scroll into
 *  view and flash. Call right before navigating to its page. */
export function requestSettingsHighlight(anchor: string): void {
  pending = { anchor, at: Date.now() };
  listeners.forEach((l) => l());
}

/**
 * Marks the calling component as the jump target for `anchor`: when a
 * requested highlight names it, `ref`'s element scrolls to center and `lit`
 * holds true for the flash. Claims the request whenever it mounts OR
 * whenever a request fires while mounted (a result on the already-open
 * page), so the skeleton→content swap on a loading page still lands the
 * flash. No-op with no anchor.
 */
export function useSettingsHighlight(anchor?: string | undefined): {
  ref: React.RefObject<HTMLDivElement | null>;
  lit: boolean;
} {
  const ref = React.useRef<HTMLDivElement>(null);
  const [lit, flash] = useTransientFlag(FLASH_MS);

  React.useEffect(() => {
    if (!anchor) return;
    const claim = () => {
      if (pending?.anchor !== anchor) return;
      if (Date.now() - pending.at > FRESH_MS) {
        pending = null;
        return;
      }
      pending = null;
      ref.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      flash();
    };
    claim();
    listeners.add(claim);
    return () => {
      listeners.delete(claim);
    };
  }, [anchor, flash]);

  return { ref, lit };
}

/**
 * The wrapper form of the hook, for targets that aren't a single
 * `SettingsGroupRow` (which takes `highlightAnchor` directly): a whole
 * settings card, a danger zone. Inert until a request names its anchor —
 * a plain full-width block that adds nothing to layout.
 */
export function SettingsHighlightTarget({
  anchor,
  children,
}: {
  anchor: string;
  children: React.ReactNode;
}) {
  const { ref, lit } = useSettingsHighlight(anchor);
  return (
    <Box
      ref={ref}
      background={lit ? "selected" : undefined}
      className="transition-colors duration-500"
      fill="width"
      rounded="md"
    >
      {children}
    </Box>
  );
}
